import { useEffect, useId, useRef, useState } from 'react';
import { Tag, VisuallyHidden } from '@dpem/design-system';
import { BellIcon, LogOutIcon, MenuIcon, SearchIcon } from 'lucide-react';
import { Link, NavLink, useLocation } from 'react-router-dom';

import { useAuth } from '@/app/providers/AuthProvider';
import { primaryRole, ROLE_LABELS } from '@/lib/auth/capabilities';
import { formatInteger } from '@/lib/format/format';
import { currentNavEntry, NAV_ENTRIES } from '@/router/surfaces';
import { PATHS } from '@/router/paths';
import { useShellChrome } from './surface-chrome';
import styles from './AppShell.module.css';

/**
 * The black band: brand, primary navigation, the open method's identity, and the
 * account.
 *
 * `data-focus-scope="inverse"` is what makes focus visible here. The token layer
 * answers it with a white ring plus a 1px inner ring, because the standard blue
 * ring is only 3.62:1 against the band and would fail WCAG 1.4.11.
 *
 * Nav entries the user cannot reach are not rendered at all rather than rendered
 * disabled. A greyed-out "Admin" tells a viewer the platform has an admin area
 * they are locked out of, which invites a support ticket and reveals structure
 * for no benefit.
 */

interface ShellHeaderProps {
  /** Toggles the icon rail. The rail is the same navigation, so it is collapsible. */
  onToggleRail: () => void;
  railExpanded: boolean;
}

export function ShellHeader({ onToggleRail, railExpanded }: ShellHeaderProps) {
  const { capabilities } = useAuth();
  const { pathname } = useLocation();
  const chrome = useShellChrome();

  const entries = NAV_ENTRIES.filter((entry) => capabilities.can(entry.capability));
  const current = currentNavEntry(pathname, entries);

  return (
    <header className={styles.header} data-focus-scope="inverse">
      <button
        type="button"
        className={`${styles.headerSquare} ${styles.menuButton}`}
        aria-label={railExpanded ? 'Collapse workspace rail' : 'Expand workspace rail'}
        aria-expanded={railExpanded}
        onClick={onToggleRail}
      >
        <MenuIcon size={16} aria-hidden="true" />
      </button>

      <Link to={PATHS.explorer} className={styles.brand}>
        <span className={styles.brandMark}>DPSE</span>
        <span className={styles.brandName}>Method Studio</span>
      </Link>

      <nav className={styles.nav} aria-label="Platform">
        {entries.map((entry) => (
          <NavLink
            key={entry.id}
            to={entry.to}
            className={styles.navLink}
            // `end` would leave a child route with no current entry, and
            // NavLink's own prefix matching cannot express "longest match wins"
            // across sibling entries — /authoring and /authoring/catalogue would
            // both be current. `currentNavEntry` decides; this only reports it.
            aria-current={current?.id === entry.id ? 'page' : undefined}
          >
            {entry.label}
          </NavLink>
        ))}
      </nav>

      <div className={styles.spacer} />

      {chrome.identity !== null && (
        <div className={styles.meta}>
          <span className={styles.pill} data-variant={chrome.identity.variant}>
            {chrome.identity.id}
          </span>
          {chrome.identity.state !== undefined && (
            <strong className={styles.metaState}>{chrome.identity.state}</strong>
          )}
          {chrome.identity.nodeCount !== undefined && (
            <span>{`· ${formatInteger(chrome.identity.nodeCount)} nodes`}</span>
          )}
        </div>
      )}

      <button type="button" className={`${styles.headerSquare} ${styles.headerIcon}`} aria-label="Search method graph">
        <SearchIcon size={16} aria-hidden="true" />
      </button>
      <button
        type="button"
        className={`${styles.headerSquare} ${styles.headerIcon}`}
        aria-label="Notifications"
      >
        <BellIcon size={16} aria-hidden="true" />
      </button>

      <AccountMenu />
    </header>
  );
}

/** Initials from a display name: two words give two letters, one gives one. */
export const initialsOf = (displayName: string): string => {
  const words = displayName.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  const first = words[0]?.[0] ?? '';
  const last = words.length > 1 ? (words[words.length - 1]?.[0] ?? '') : '';
  return `${first}${last}`.toUpperCase();
};

function AccountMenu() {
  const { user, capabilities, logout, pending } = useAuth();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  // Escape closes, and a click anywhere else closes: a menu that can only be
  // dismissed by hitting its own trigger again is a trap on a touch device.
  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setOpen(false);
    };
    const onPointerDown = (event: MouseEvent): void => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [open]);

  if (user === null) return null;

  const role = primaryRole(capabilities.roles);

  return (
    <div className={styles.account} ref={containerRef}>
      <button
        type="button"
        className={styles.accountButton}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => setOpen((value) => !value)}
      >
        <span className={styles.avatar} aria-hidden="true">
          {initialsOf(user.displayName)}
        </span>
        <VisuallyHidden>
          {`Account: ${user.displayName}${role === null ? '' : `, ${ROLE_LABELS[role]}`}`}
        </VisuallyHidden>
      </button>

      {open && (
        <div className={styles.accountMenu} id={menuId} role="menu">
          <div className={styles.accountIdentity}>
            <p className={styles.accountName}>{user.displayName}</p>
            <p className={styles.accountEmail}>{user.email}</p>
            <div className={styles.accountRoles}>
              {/* Grey, not coloured: a role is not a method classification, and
                  §6.2 says colour classifies or it is grey. */}
              {capabilities.roles.map((each) => (
                <Tag key={each} size="sm" variant="outline">
                  {ROLE_LABELS[each]}
                </Tag>
              ))}
            </div>
          </div>
          <button
            type="button"
            role="menuitem"
            className={styles.accountAction}
            disabled={pending}
            onClick={() => {
              setOpen(false);
              void logout();
            }}
          >
            <LogOutIcon size={16} aria-hidden="true" />
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
